/**
 * PANACHE - Shop the Look (Reformation theme)
 *
 * Theme integration notes. All learned the hard way; do not undo these.
 *
 * PLACEMENT. The gallery is <product-slider-thumbnails>, a Flickity carousel.
 * Slides (.product-images__slide) are hidden or repositioned on layout, so
 * anything injected inside them vanishes. The trigger goes in as a sibling
 * AFTER the gallery element.
 *
 * OPENING. We do not use the theme's <view-products>. It caches its drawer
 * reference on connect and goes stale when the editor re-renders the section.
 * We resolve the drawer fresh on every click.
 *
 * CLOSING. We do not use <side-panel-close>. It calls OverlayManager.closeLast(),
 * which closes whatever is top of a SHARED stack, not necessarily our drawer.
 * We call OverlayManager.close(ourPanel) explicitly.
 *
 * CART. We do NOT dispatch 'cart:refresh'. The theme's <cart-drawer> listens
 * for it and re-fetches itself, duplicating work we have already done and
 * racing our own DOM update. We mirror the theme's own pattern instead:
 * replace the cart sections, then dispatch 'cart:item-added'.
 *
 * ATTRIBUTION. Items added from the drawer carry a hidden line item property
 * _shop_the_look set to the handle of the product page the drawer was opened
 * from. Underscore prefix keeps it out of cart and checkout display while
 * remaining visible on the order in admin and in exports. Shopify Flow reads
 * it to apply an order tag. NOTE: a line item with a property will not merge
 * with the same variant added without one - that is expected and is what
 * makes the attribution work.
 *
 * All listeners are delegated from document so a section re-render cannot
 * orphan them.
 */

(function () {
  'use strict';

  var DRAWER_ID = 'Panache-Look-Drawer';
  var SOURCE_PROPERTY = '_shop_the_look';

  var GALLERY_SELECTORS = [
    'product-slider-thumbnails',
    '.product-images',
    '.thb-product-detail .product-single__media-group',
    '.product-single__photos'
  ];

  var MAX_ATTEMPTS = 40;
  var RETRY_DELAY = 150;

  function om() {
    return window.OverlayManager || null;
  }

  /* ------------------------------------------------------------------ */
  /* Attribution                                                         */
  /* ------------------------------------------------------------------ */

  /* Handle of the product page the drawer was opened from. Prefer the
     canonical link, fall back to the path. Works on collection-scoped
     product URLs too, since those still contain /products/<handle>. */
  function sourceHandle() {
    var path = '';

    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && canonical.href) {
      try { path = new URL(canonical.href).pathname; } catch (e) { path = ''; }
    }
    if (!path) path = window.location.pathname;

    var marker = '/products/';
    var i = path.indexOf(marker);
    if (i === -1) return 'unknown';

    var handle = path.slice(i + marker.length).split('/')[0].split('?')[0];
    return handle || 'unknown';
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function purgeDetachedPanels() {
    var manager = om();
    if (!manager || !manager.stack) return;

    for (var i = manager.stack.length - 1; i >= 0; i--) {
      var panel = manager.stack[i];
      if (!panel || !panel.isConnected) manager.stack.splice(i, 1);
    }

    if (!manager.stack.length) document.body.classList.remove('open-cc');
  }

  function dedupe() {
    var panels = document.querySelectorAll('#' + DRAWER_ID);
    for (var i = 0; i < panels.length - 1; i++) panels[i].remove();

    var triggers = document.querySelectorAll('panache-stl-trigger[data-relocated="true"]');
    for (var j = 0; j < triggers.length - 1; j++) triggers[j].remove();

    purgeDetachedPanels();

    return panels.length ? panels[panels.length - 1] : null;
  }

  function getDrawer() {
    var panels = document.querySelectorAll('#' + DRAWER_ID);
    if (panels.length > 1) return dedupe();
    return panels[0] || null;
  }

  function setExpanded(state) {
    var triggers = document.querySelectorAll('[data-stl-open]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', state ? 'true' : 'false');
    }
  }

  function openDrawer() {
    purgeDetachedPanels();

    var panel = getDrawer();
    if (!panel) return;

    var manager = om();
    if (manager && typeof manager.open === 'function') {
      manager.open(panel);
    } else {
      panel.inert = false;
      panel.classList.add('active');
      document.body.classList.add('open-cc');
      panel.focus();
    }

    setExpanded(true);
  }

  function closeDrawer() {
    var panel = getDrawer();
    if (!panel) return;

    var manager = om();
    if (manager && typeof manager.close === 'function') {
      manager.close(panel);
    } else {
      panel.inert = true;
      panel.classList.remove('active');
      document.body.classList.remove('open-cc');
    }

    setExpanded(false);
  }

  /* ------------------------------------------------------------------ */
  /* 1. Trigger placement                                                */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('panache-stl-trigger')) {
    class PanacheSTLTrigger extends HTMLElement {
      connectedCallback() {
        this.attempts = 0;
        this.tryRelocate();

        if (document.readyState !== 'complete') {
          window.addEventListener('load', this.tryRelocate.bind(this), { once: true });
        }
      }

      tryRelocate() {
        if (this.dataset.relocated === 'true' || !this.isConnected) return;

        var target = this.findTarget();

        if (target && target.offsetWidth > 0) {
          this.place(target);
          return;
        }

        this.attempts++;

        if (this.attempts < MAX_ATTEMPTS) {
          setTimeout(this.tryRelocate.bind(this), RETRY_DELAY);
          return;
        }

        if (target) this.place(target);
      }

      findTarget() {
        var custom = (this.dataset.targetSelector || '').trim();
        var candidates = custom ? [custom].concat(GALLERY_SELECTORS) : GALLERY_SELECTORS;

        for (var i = 0; i < candidates.length; i++) {
          var el;
          try {
            el = document.querySelector(candidates[i]);
          } catch (e) {
            continue;
          }
          if (!el) continue;
          if (el.closest('.product-images__slide')) continue;
          return el;
        }
        return null;
      }

      place(target) {
        var existing = document.querySelectorAll('panache-stl-trigger[data-relocated="true"]');
        for (var i = 0; i < existing.length; i++) {
          if (existing[i] !== this) existing[i].remove();
        }

        target.insertAdjacentElement('afterend', this);
        this.classList.add('is-relocated');
        this.dataset.relocated = 'true';
      }
    }

    customElements.define('panache-stl-trigger', PanacheSTLTrigger);
  }

  /* ------------------------------------------------------------------ */
  /* 2. Theme editor recovery                                            */
  /* ------------------------------------------------------------------ */

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.classList) return;
    if (!event.target.classList.contains('panache-stl-section')) return;
    setTimeout(function () { dedupe(); setExpanded(false); }, 0);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target || !event.target.classList) return;
    if (!event.target.classList.contains('panache-stl-section')) return;
    setTimeout(dedupe, 0);
  });

  /* ------------------------------------------------------------------ */
  /* 3. Open and close                                                   */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    if (event.target.closest('[data-stl-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-stl-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.target.closest('.click-capture')) {
      setTimeout(function () {
        var panel = getDrawer();
        var manager = om();
        if (!panel || !manager || !manager.isOpen || !manager.isOpen(panel)) setExpanded(false);
      }, 0);
    }
  });

  document.addEventListener('keyup', function (event) {
    if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
    setTimeout(function () {
      var panel = getDrawer();
      var manager = om();
      if (!panel || !manager || !manager.isOpen || !manager.isOpen(panel)) setExpanded(false);
    }, 0);
  });

  /* ------------------------------------------------------------------ */
  /* 4. Inline add to cart                                               */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var sizeBtn = event.target.closest && event.target.closest('[data-stl-variant]');
    if (!sizeBtn || sizeBtn.disabled) return;

    var card = sizeBtn.closest('[data-stl-card]');
    if (!card) return;

    card.querySelectorAll('[data-stl-variant]').forEach(function (b) {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
    sizeBtn.classList.add('is-selected');
    sizeBtn.setAttribute('aria-pressed', 'true');

    var input = card.querySelector('[data-stl-variant-input]');
    if (input) input.value = sizeBtn.dataset.stlVariant;

    hideMessage(card);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('[data-stl-form]');
    if (!form) return;
    event.preventDefault();

    var card = form.closest('[data-stl-card]');
    var input = form.querySelector('[data-stl-variant-input]');
    var button = form.querySelector('[data-stl-add]');
    var variantId = input ? input.value : '';

    if (!variantId) {
      showMessage(card, 'Choose a size', true);
      var sizes = card.querySelector('.panache-stl-card__sizes');
      if (sizes) {
        sizes.classList.add('is-nudged');
        setTimeout(function () { sizes.classList.remove('is-nudged'); }, 500);
      }
      return;
    }

    addToCart(variantId, card, button);
  });

  function showMessage(card, text, isError) {
    if (!card) return;
    var el = card.querySelector('[data-stl-message]');
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('is-error', !!isError);
    el.hidden = false;
  }

  function hideMessage(card) {
    if (!card) return;
    var el = card.querySelector('[data-stl-message]');
    if (el) el.hidden = true;
  }

  function cartAddUrl() {
    if (window.theme && window.theme.routes && window.theme.routes.cart_add_url) {
      return window.theme.routes.cart_add_url;
    }
    return '/cart/add';
  }

  function addToCart(variantId, card, button) {
    if (card) card.classList.add('is-loading');
    if (button) button.setAttribute('disabled', 'disabled');
    hideMessage(card);

    var properties = {};
    properties[SOURCE_PROPERTY] = sourceHandle();

    fetch(cartAddUrl() + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties: properties
        }],
        sections: ['cart-drawer', 'cart-bubble'],
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          showMessage(card, result.data.description || result.data.message || 'Could not add that one.', true);
          return;
        }

        renderCartSections(result.data.sections);

        try {
          if (typeof window.dispatchCustomEvent === 'function') {
            window.dispatchCustomEvent('cart:item-added', {
              product: result.data.items ? result.data.items[0] : result.data
            });
          }
        } catch (e) { /* non-fatal */ }

        showMessage(card, 'Added', false);

        if (button) {
          var label = button.querySelector('[data-stl-add-label]');
          if (label) {
            var original = label.textContent;
            label.textContent = 'Added';
            setTimeout(function () { label.textContent = original; }, 1800);
          }
        }
      })
      .catch(function () {
        showMessage(card, 'Something went wrong. Try again.', true);
      })
      .finally(function () {
        if (card) card.classList.remove('is-loading');
        if (button) button.removeAttribute('disabled');
      });
  }

  function renderCartSections(sections) {
    if (!sections) return;

    var targets = [
      { section: 'cart-drawer', id: 'Cart-Drawer', selector: '.cart-drawer' },
      { section: 'cart-bubble', id: 'cart-drawer-toggle', selector: '.thb-item-count' }
    ];

    targets.forEach(function (t) {
      var html = sections[t.section];
      if (!html) return;

      var container = document.getElementById(t.id);
      if (!container) return;

      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var source = parsed.querySelector(t.selector);
      if (!source) return;

      var destination = container.querySelector(t.selector) || container;
      destination.innerHTML = source.innerHTML;
    });
  }
})();
